import type { Ref } from "react";

import { timer } from "~/components/TimerChip.css";

export type TimerLevel = "nominal" | "warning" | "breach";

/** mm:ss under an hour, h:mm beyond. */
export function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) {
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * THE threshold ladder. Every "is this late?" question in the app
 * resolves here — the chip's own coloring, the breach counters, the
 * card accents — so that a caller can never disagree with what the
 * user is looking at.
 *
 * Thresholds are CONFIGURATION, not constants: callers pass the pair
 * they loaded; this function only knows nominal → warning → breach, and
 * an omitted threshold simply never fires.
 */
export function timerLevel(
  elapsedMs: number,
  warnMs?: number | undefined,
  breachMs?: number | undefined,
): TimerLevel {
  if (breachMs !== undefined && elapsedMs >= breachMs) return "breach";
  if (warnMs !== undefined && elapsedMs >= warnMs) return "warning";
  return "nominal";
}

export interface TimerChipProps {
  elapsedMs: number;
  warnMs?: number | undefined;
  breachMs?: number | undefined;
  /** Tooltip text — say what the clock is measuring, not what it reads. */
  title?: string | undefined;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * A running clock. Chip geometry appears only at warning and breach:
 * nominal timers are quiet numerals, so an exception is the only thing
 * on the screen that shouts.
 */
export function TimerChip({
  elapsedMs,
  warnMs,
  breachMs,
  title,
  ref,
}: TimerChipProps) {
  const level = timerLevel(elapsedMs, warnMs, breachMs);
  return (
    <span ref={ref} className={timer[level]} title={title} data-testid="timer-chip">
      {formatElapsed(elapsedMs)}
    </span>
  );
}
